import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GiftCardReviewModal from '../components/GiftCardReviewModal';
import JsonBlock from '../components/JsonBlock';
import { getGiftCardRedemptionById } from '../services/giftCardService';
import {
  emitActionNeededCount,
  formatDenomination,
  formatNumber,
  formatUsd,
  getCardOrderedDisplay,
  getEnvDisplay,
  getStatusDisplay,
  XOXODAY_SETTINGS_PATH,
} from '../constants/giftCard';

const NOTE_MAX_LENGTH = 500;
const TOAST_DURATION_MS = 4000;

const OUTCOME_DISPLAY = {
  found_existing: { label: 'Found existing order', className: 'success' },
  accepted: { label: 'Accepted by Xoxoday', className: 'success' },
  rejected: { label: 'Rejected', className: 'failed' },
  order_rejected: { label: 'Rejected', className: 'failed' },
  unconfirmed: { label: 'No confirmation', className: 'gc-pending' },
  lookup_failed: { label: "Couldn't check order", className: 'failed' },
};

const ADMIN_OUTCOME_DISPLAY = {
  approved: { label: 'Admin approved', className: 'success' },
  rejected: { label: 'Admin rejected', className: 'failed' },
};

const ERROR_STAGE_LABELS = {
  order_rejected: 'Rejected by Xoxoday',
  unconfirmed: 'No confirmation from Xoxoday (timeout)',
  lookup_failed: "Couldn't check order with Xoxoday",
  webhook_failed: 'Xoxoday reported failure',
  unexpected_error: 'Server error',
  retry_error: 'Retry crashed',
};

const REVIEW_ACTION_LABELS = {
  approve: 'Approved',
  reject: 'Rejected',
};

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  const day = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${day} · ${time}`;
}

function isAdminAttempt(entry) {
  return String(entry?.attempt ?? '').toLowerCase() === 'admin';
}

function getOutcomeDisplay(entry) {
  const key = String(entry?.outcome || '').toLowerCase();
  if (isAdminAttempt(entry) && ADMIN_OUTCOME_DISPLAY[key]) return ADMIN_OUTCOME_DISPLAY[key];
  return OUTCOME_DISPLAY[key] || { label: entry?.outcome || '—', className: 'gc-pending' };
}

function getErrorStageLabel(stage) {
  const key = String(stage || '').toLowerCase();
  if (key.endsWith('_after_3_retries')) return 'Retries exhausted';
  return ERROR_STAGE_LABELS[key] || stage || '—';
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function sortNewestFirst(entries) {
  // Logs are appended oldest-first; reverse, then sort by timestamp where both have one.
  return [...entries].reverse().sort((a, b) => {
    const ta = new Date(a?.at).getTime();
    const tb = new Date(b?.at).getTime();
    if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
    return tb - ta;
  });
}

function DetailItem({ label, children, mono = false }) {
  return (
    <div className="detail-item">
      <span className="detail-label">{label}</span>
      <span className={`detail-value ${mono ? 'detail-value-mono' : ''}`.trim()}>{children}</span>
    </div>
  );
}

function EnvBadge({ environment }) {
  const env = getEnvDisplay(environment);
  if (!env) return null;
  return <span className={`gc-env-badge ${env.className}`}>{env.label}</span>;
}

// Plain-language explanation of an "Action needed" order; raw codes stay in the collapsed toggle.
function ProblemCard({ problem, onOpenProviderSettings }) {
  const cardOrdered = getCardOrderedDisplay(problem.cardOrdered);
  const steps = asArray(problem.steps);

  return (
    <div className="user-detail-card gc-problem-card">
      <h3 className="gc-problem-title">{problem.title || 'This order needs a decision'}</h3>
      {problem.summary ? <p className="gc-problem-summary">{problem.summary}</p> : null}
      {cardOrdered ? (
        <span className={`status-badge status-${cardOrdered.className}`}>{cardOrdered.label}</span>
      ) : null}

      {steps.length > 0 ? (
        <>
          <h5 className="gc-subsection-title">What to do</h5>
          <ol className="gc-problem-steps">
            {steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        </>
      ) : null}

      {problem.xoxodayMessage ? (
        <p className="gc-problem-xoxoday">Xoxoday said: “{problem.xoxodayMessage}”</p>
      ) : null}

      {problem.code === 'xoxoday_token_invalid' ? (
        <div className="trip-detail-actions gc-review-actions gc-problem-actions">
          <button type="button" className="trip-action-btn" onClick={onOpenProviderSettings}>
            Open Provider Settings
          </button>
        </div>
      ) : null}

      {problem.technical ? (
        <details className="gc-problem-technical">
          <summary>Technical details</summary>
          <div className="gift-card-retry-error">{problem.technical}</div>
        </details>
      ) : null}
    </div>
  );
}

export default function GiftCardRedemptionDetail() {
  const { redemptionId } = useParams();
  const navigate = useNavigate();

  const [redemption, setRedemption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [reviewAction, setReviewAction] = useState(null);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const fetchRedemption = useCallback(async () => {
    const response = await getGiftCardRedemptionById(redemptionId);
    return response.data || null;
  }, [redemptionId]);

  useEffect(() => {
    let cancelled = false;

    async function loadRedemption() {
      setLoading(true);
      setError('');
      setNote('');

      try {
        const data = await fetchRedemption();
        if (cancelled) return;
        setRedemption(data);
      } catch (err) {
        if (cancelled) return;
        setError(err.status === 404 ? 'Gift card redemption not found.' : err.message || 'Failed to load gift card redemption.');
        setRedemption(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadRedemption();
    return () => {
      cancelled = true;
    };
  }, [fetchRedemption]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  function showToast(text, kind) {
    clearTimeout(toastTimer.current);
    setToast({ text, kind });
    toastTimer.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }

  function handleReviewed(message, { conflict, redemption: updated }) {
    showToast(message, conflict ? 'warning' : 'success');
    if (updated?.id) setRedemption(updated);
    setNote('');
    emitActionNeededCount();
    fetchRedemption()
      .then((data) => {
        if (data) setRedemption(data);
      })
      .catch(() => {});
  }

  function goBack() {
    navigate('/gift-card-redemptions');
  }

  if (loading) {
    return (
      <div className="gift-card-detail-page main-bg-top">
        <div className="user-detail-loading">Loading gift card redemption...</div>
      </div>
    );
  }

  if (error || !redemption) {
    return (
      <div className="gift-card-detail-page main-bg-top">
        <div className="user-detail-error">
          <p>{error || 'Gift card redemption not found.'}</p>
          <button className="btn btn-primary" type="button" onClick={goBack}>
            Back to Gift Card Redemptions
          </button>
        </div>
      </div>
    );
  }

  const status = getStatusDisplay(redemption);
  const canReview = redemption.canReview === true;
  const petalsText = formatNumber(redemption.petalsSpent);
  const userName = redemption.user?.name || '—';
  const userInitial = userName !== '—' ? userName.charAt(0).toUpperCase() : '?';
  const errorLog = sortNewestFirst(asArray(redemption.errorLog));
  const historyLog = sortNewestFirst(asArray(redemption.retryLog));
  const webhookLog = asArray(redemption.webhookLog);
  const review = redemption.review;
  const requestPayload = redemption.requestPayload;
  const pricing = requestPayload?.pricing;
  const problem = redemption.problem || null;

  return (
    <div className="gift-card-detail-page main-bg-top">
      <div className="coupons-top-bar">
        <button className="coupons-back-btn" type="button" onClick={goBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      <div className="user-detail-content">
        <div className="user-detail-card">
          <div className="trip-detail-summary">
            <div>
              <h3 className="trip-detail-title">{redemption.productName || 'Gift Card Order'}</h3>
              <p className="gift-card-detail-po">{redemption.poNumber || '—'}</p>
            </div>
            <div className="trip-detail-badges">
              <div className="trip-detail-badge-row">
                <span className={`status-badge status-${status.className}`}>{status.label}</span>
                <EnvBadge environment={redemption.xoxodayEnvironment} />
              </div>
            </div>
          </div>
        </div>

        {problem ? (
          <ProblemCard problem={problem} onOpenProviderSettings={() => navigate(XOXODAY_SETTINGS_PATH)} />
        ) : null}

        {canReview ? (
          <div className="user-detail-card gc-review-panel">
            <h4 className="detail-card-title">Decision</h4>
            <p className="gc-review-help">
              Check this order with Xoxoday support using the PO number, then decide.
            </p>
            <label className="add-restaurant-field gift-card-review-field">
              <span>Note (optional)</span>
              <textarea
                rows={3}
                value={note}
                maxLength={NOTE_MAX_LENGTH}
                placeholder="What did Xoxoday support say?"
                onChange={(e) => setNote(e.target.value)}
                disabled={Boolean(reviewAction)}
              />
              <span className="gc-review-count">{note.length}/{NOTE_MAX_LENGTH}</span>
            </label>
            <div className="trip-detail-actions gc-review-actions">
              <button
                type="button"
                className="trip-action-btn gc-review-approve"
                onClick={() => setReviewAction('approve')}
                disabled={Boolean(reviewAction)}
              >
                Approve — mark as sent
              </button>
              <button
                type="button"
                className="trip-action-btn trip-action-reject gc-review-reject"
                onClick={() => setReviewAction('reject')}
                disabled={Boolean(reviewAction)}
              >
                Reject — refund {petalsText} petals
              </button>
            </div>
          </div>
        ) : null}

        {review ? (
          <div className="user-detail-card">
            <h4 className="detail-card-title">Decision</h4>
            <div className="detail-grid">
              <DetailItem label="Decision">
                {REVIEW_ACTION_LABELS[String(review.action || '').toLowerCase()] || review.action || '—'}
              </DetailItem>
              <DetailItem label="Decided At">{formatDateTime(review.at)}</DetailItem>
              <DetailItem label="Admin ID" mono>{review.byAdminId || '—'}</DetailItem>
            </div>
            <div className="gift-card-review-note">
              <span className="detail-label">Note</span>
              <p>{review.note || '—'}</p>
            </div>
          </div>
        ) : null}

        <div className="user-detail-card">
          <h4 className="detail-card-title">Summary</h4>
          {redemption.pricingNote ? <p className="gc-pricing-note">{redemption.pricingNote}</p> : null}
          <div className="detail-grid">
            <DetailItem label="Denomination">
              {formatDenomination(redemption.denomination, redemption.currencyCode)}
            </DetailItem>
            <DetailItem label="USD Value">{formatUsd(redemption.usdValue)}</DetailItem>
            <DetailItem label="Coins Spent">{formatNumber(redemption.coinsSpent)}</DetailItem>
            <DetailItem label="Petals Spent">{formatNumber(redemption.petalsSpent)}</DetailItem>
            <DetailItem label="Created">{formatDateTime(redemption.createdAt)}</DetailItem>
          </div>
        </div>

        <div className="user-detail-card">
          <h4 className="detail-card-title">Order</h4>
          <div className="detail-grid">
            <DetailItem label="PO Number" mono>{redemption.poNumber || '—'}</DetailItem>
            <DetailItem label="Order ID" mono>{redemption.orderId || '—'}</DetailItem>
            <DetailItem label="Redemption ID" mono>{redemption.id || '—'}</DetailItem>
            <DetailItem label="Product">{redemption.productName || '—'}</DetailItem>
            <DetailItem label="Product ID" mono>{redemption.productId || '—'}</DetailItem>
            <DetailItem label="Recipient Email">{redemption.recipientEmail || '—'}</DetailItem>
            <DetailItem label="Currency">{redemption.currencyCode || '—'}</DetailItem>
            <DetailItem label="Xoxoday Order Status">{redemption.orderStatus || '—'}</DetailItem>
            <DetailItem label="Delivery Status">{redemption.deliveryStatus || '—'}</DetailItem>
            <DetailItem label="Amount Charged (USD)">{formatUsd(redemption.amountCharged)}</DetailItem>
          </div>
        </div>

        <div className="user-detail-card">
          <h4 className="detail-card-title">Buyer</h4>
          <div className="txn-detail-user-banner">
            <div className="txn-detail-user-avatar">{userInitial}</div>
            <div className="txn-detail-user-meta">
              <span className="txn-detail-user-name">{userName}</span>
              <span className="txn-detail-user-email">{redemption.user?.email || '—'}</span>
            </div>
          </div>
          <div className="detail-grid">
            <DetailItem label="User ID" mono>{redemption.user?.id || '—'}</DetailItem>
            <DetailItem label="Coins Spent">{formatNumber(redemption.coinsSpent)}</DetailItem>
            <DetailItem label="Petals Spent">{formatNumber(redemption.petalsSpent)}</DetailItem>
          </div>
        </div>

        <div className="user-detail-card">
          <h4 className="detail-card-title">Timeline</h4>
          <div className="detail-grid">
            <DetailItem label="Created">{formatDateTime(redemption.createdAt)}</DetailItem>
            <DetailItem label="Updated">{formatDateTime(redemption.updatedAt)}</DetailItem>
            <DetailItem label="User told 24-48h">{formatDateTime(redemption.notifiedInProgressAt)}</DetailItem>
            <DetailItem label="Refunded">{formatDateTime(redemption.refundedAt)}</DetailItem>
          </div>
        </div>

        {/* For developers — the problem card above is what admins act on. */}
        <details className="gc-technical-section">
          <summary className="gc-technical-section-summary">Technical details</summary>
          <div className="user-detail-content">
            <div className="user-detail-card">
              <h4 className="detail-card-title">Errors ({errorLog.length})</h4>
              {errorLog.length === 0 ? (
                <div className="gift-card-retry-empty">No errors recorded</div>
              ) : (
                <ul className="gift-card-retry-list">
                  {errorLog.map((entry, index) => (
                    <li key={`${entry.at || 'error'}-${index}`} className="gift-card-retry-item">
                      <div className="gift-card-retry-head">
                        <span className="gift-card-retry-attempt">{getErrorStageLabel(entry.stage)}</span>
                        <EnvBadge environment={entry.environment} />
                        <span className="gift-card-retry-time">{formatDateTime(entry.at)}</span>
                      </div>
                      {entry.message && <div className="gift-card-retry-error">{entry.message}</div>}
                      <JsonBlock label="Xoxoday response" value={entry.details} />
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {requestPayload ? (
              <div className="user-detail-card">
                <h4 className="detail-card-title">Request</h4>
                <div className="detail-grid">
                  <DetailItem label="Pricing">
                    {pricing?.source === 'live' ? 'Live price' : pricing?.source === 'local_catalog' ? 'Saved catalog' : '—'}
                  </DetailItem>
                  <DetailItem label="Xoxoday Host" mono>{requestPayload.xoxoday?.host || '—'}</DetailItem>
                </div>

                <h5 className="gc-subsection-title">What the user asked for</h5>
                <div className="detail-grid">
                  <DetailItem label="Product ID" mono>{requestPayload.request?.productId ?? '—'}</DetailItem>
                  <DetailItem label="Denomination">
                    {formatDenomination(requestPayload.request?.denomination, redemption.currencyCode)}
                  </DetailItem>
                  <DetailItem label="Recipient Email">{requestPayload.request?.recipientEmail || '—'}</DetailItem>
                  <DetailItem label="Expected Coins">{formatNumber(requestPayload.request?.expectedCoins)}</DetailItem>
                  <DetailItem label="Idempotency Key" mono>{requestPayload.request?.idempotencyKey || '—'}</DetailItem>
                </div>

                <h5 className="gc-subsection-title">What was charged</h5>
                <div className="detail-grid">
                  <DetailItem label="Coins">{formatNumber(requestPayload.priced?.coins)}</DetailItem>
                  <DetailItem label="Petals">{formatNumber(requestPayload.priced?.petals)}</DetailItem>
                  <DetailItem label="USD Value">{formatUsd(requestPayload.priced?.usdValue)}</DetailItem>
                </div>

                <JsonBlock label="Sent to Xoxoday" value={requestPayload.order} />
              </div>
            ) : null}

            <div className="user-detail-card">
              <h4 className="detail-card-title">History ({historyLog.length})</h4>
              {historyLog.length === 0 ? (
                <div className="gift-card-retry-empty">No attempts recorded.</div>
              ) : (
                <ul className="gift-card-retry-list">
                  {historyLog.map((entry, index) => {
                    const outcome = getOutcomeDisplay(entry);
                    return (
                      <li key={`${entry.at || index}-${entry.attempt ?? index}`} className="gift-card-retry-item">
                        <div className="gift-card-retry-head">
                          <span className="gift-card-retry-attempt">
                            {isAdminAttempt(entry) ? 'Admin' : `Attempt ${entry.attempt ?? '—'}`}
                          </span>
                          <span className={`status-badge status-${outcome.className}`}>{outcome.label}</span>
                          <span className="gift-card-retry-time">{formatDateTime(entry.at)}</span>
                        </div>
                        {entry.error && <div className="gift-card-retry-error">{entry.error}</div>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="user-detail-card">
              <h4 className="detail-card-title">Webhooks ({webhookLog.length})</h4>
              {webhookLog.length === 0 ? (
                <div className="gift-card-retry-empty">No updates received from Xoxoday</div>
              ) : (
                <ul className="gift-card-retry-list">
                  {webhookLog.map((entry, index) => (
                    <li key={`${entry.at || 'webhook'}-${index}`} className="gift-card-retry-item">
                      <div className="gift-card-retry-head">
                        <span className="gift-card-retry-attempt">{entry.outcome || '—'}</span>
                        <span className="gift-card-retry-time">{formatDateTime(entry.at)}</span>
                      </div>
                      <div className="detail-grid gc-webhook-grid">
                        <DetailItem label="Delivery Status">{entry.deliveryStatus || '—'}</DetailItem>
                        <DetailItem label="Order Status">{entry.orderStatus || '—'}</DetailItem>
                        <DetailItem label="Status Before">{entry.statusBefore || '—'}</DetailItem>
                      </div>
                      {entry.error && <div className="gift-card-retry-error">{entry.error}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="user-detail-card">
              <details className="gc-technical">
                <summary className="detail-card-title gc-technical-summary">Technical</summary>
                <div className="detail-grid">
                  <DetailItem label="Raw Status" mono>{redemption.status || '—'}</DetailItem>
                  <DetailItem label="ID" mono>{redemption.id || '—'}</DetailItem>
                  <DetailItem label="Updated">{formatDateTime(redemption.updatedAt)}</DetailItem>
                </div>
              </details>
            </div>
          </div>
        </details>
      </div>

      <GiftCardReviewModal
        open={Boolean(reviewAction)}
        redemptionId={redemption.id}
        action={reviewAction}
        note={note}
        petals={redemption.petalsSpent}
        onClose={() => setReviewAction(null)}
        onReviewed={handleReviewed}
      />

      {toast ? (
        <div className={`gc-toast gc-toast-${toast.kind}`} role="status">{toast.text}</div>
      ) : null}
    </div>
  );
}
